import { Injectable } from '@angular/core';
import type { ISourceOptions } from '@tsparticles/engine';

@Injectable({
  providedIn: 'root'
})
export class ParticlesService {

  /** Estilo host compartido para fondo absoluto sobre el banner */
  readonly hostStyle: Record<string, string> = {
    top: '0',
    left: '0',
    width: '100%',
    height: '100%',
    position: 'absolute',
    'z-index': '0'
  };

  /** Color de marca del sitio (--mainColor). */
  private readonly brandGreen = '#26a849';

  /** Default: red suave + se UNEN al pasar el cursor (grab). */
  getDefaultOptions(): ISourceOptions {
    return {
      fullScreen: { enable: false },
      background: { color: { value: 'transparent' } },
      fpsLimit: 60,
      particles: {
        number: { value: 60, density: { enable: true, width: 800, height: 800 } },
        // v4: el color va en paint (particles.color ya no aplica)
        paint: {
          color: { value: '#ffffff' },
          fill: { enable: true, opacity: 0.38 },
          stroke: { width: 0 }
        },
        shape: { type: 'triangle' },
        opacity: { value: 0.38 },
        size: { value: { min: 1, max: 2.8 } },
        links: {
          enable: true,
          distance: 135,
          color: '#ffffff',
          opacity: 0.22,
          width: 0.8
        },
        move: { enable: true, speed: 1.5, direction: 'none', outModes: { default: 'out' } }
      },
      interactivity: {
        detectsOn: 'canvas',
        events: {
          onHover: { enable: true, mode: 'grab' },
          onClick: { enable: true, mode: 'push' }
        },
        modes: {
          grab: { distance: 180, links: { opacity: 0.55 } },
          push: { quantity: 3 }
        }
      },
      detectRetina: true
    };
  }

  /**
   * Tecnología: hexágonos verdes de marca, sin malla, hover = bubble.
   */
  getTechOptions(): ISourceOptions {
    return {
      fullScreen: { enable: false },
      background: { color: { value: 'transparent' } },
      fpsLimit: 60,
      particles: {
        number: { value: 50, density: { enable: true, width: 800, height: 800 } },
        paint: {
          color: { value: this.brandGreen },
          fill: { enable: true, opacity: { min: 0.45, max: 0.9 } },
          stroke: { width: 0 }
        },
        shape: {
          type: 'polygon',
          options: {
            polygon: {
              sides: 6
            }
          }
        },
        opacity: { value: { min: 0.45, max: 0.9 } },
        size: { value: { min: 3, max: 8 } },
        links: { enable: false },
        move: {
          enable: true,
          speed: 1.2,
          direction: 'none',
          random: true,
          straight: false,
          outModes: { default: 'out' }
        }
      },
      interactivity: {
        detectsOn: 'canvas',
        events: {
          onHover: { enable: true, mode: 'bubble' },
          onClick: { enable: true, mode: 'repulse' }
        },
        modes: {
          bubble: {
            distance: 180,
            size: 14,
            duration: 1.5,
            opacity: 1
          },
          repulse: { distance: 140, duration: 0.4 }
        }
      },
      detectRetina: true
    };
  }

  getParticleTheme(theme: string): ISourceOptions {
    const themes: Record<string, ISourceOptions> = {
      default: this.getDefaultOptions(),
      tech: this.getTechOptions(),

      cosmic: {
        fullScreen: { enable: false },
        background: { color: { value: 'transparent' } },
        fpsLimit: 60,
        particles: {
          number: { value: 120, density: { enable: true, width: 800, height: 800 } },
          color: { value: '#007bff' },
          shape: { type: 'star' },
          opacity: { value: { min: 0.2, max: 0.6 } },
          size: { value: { min: 0.5, max: 2 } },
          links: { enable: true, distance: 80, color: '#007bff', opacity: 0.2, width: 1 },
          move: { enable: true, speed: 2, direction: 'none', random: true, outModes: { default: 'out' } }
        },
        interactivity: {
          detectsOn: 'canvas',
          events: {
            onHover: { enable: true, mode: 'bubble' },
            onClick: { enable: true, mode: 'push' }
          },
          modes: {
            bubble: { distance: 200, size: 4, duration: 2, opacity: 0.8 },
            push: { quantity: 4 }
          }
        },
        detectRetina: true
      },

      nature: {
        fullScreen: { enable: false },
        background: { color: { value: 'transparent' } },
        fpsLimit: 60,
        particles: {
          number: { value: 60, density: { enable: true, width: 800, height: 800 } },
          color: { value: ['#2ecc71', '#27ae60', '#16a085', '#1abc9c'] },
          shape: { type: 'circle' },
          opacity: { value: { min: 0.4, max: 0.8 } },
          size: { value: { min: 2, max: 6 } },
          links: { enable: true, distance: 200, color: '#2ecc71', opacity: 0.3, width: 1 },
          move: { enable: true, speed: 1.5, direction: 'none', random: true, outModes: { default: 'out' } }
        },
        interactivity: {
          detectsOn: 'canvas',
          events: {
            onHover: { enable: true, mode: 'grab' },
            onClick: { enable: true, mode: 'bubble' }
          },
          modes: {
            grab: { distance: 200, links: { opacity: 1 } },
            bubble: { distance: 200, size: 8, duration: 2, opacity: 0.8 }
          }
        },
        detectRetina: true
      },

      fire: {
        fullScreen: { enable: false },
        background: { color: { value: 'transparent' } },
        fpsLimit: 60,
        particles: {
          number: { value: 100, density: { enable: true, width: 800, height: 800 } },
          color: { value: ['#ff4500', '#ff6347', '#ff8c00', '#ffa500'] },
          shape: { type: 'circle' },
          opacity: { value: { min: 0.5, max: 0.9 } },
          size: { value: { min: 2, max: 6 } },
          links: { enable: false },
          move: { enable: true, speed: 3, direction: 'top', random: true, outModes: { default: 'out' } }
        },
        interactivity: {
          detectsOn: 'canvas',
          events: {
            onHover: { enable: true, mode: 'bubble' },
            onClick: { enable: true, mode: 'push' }
          },
          modes: {
            bubble: { distance: 200, size: 10, duration: 2, opacity: 0.8 },
            push: { quantity: 4 }
          }
        },
        detectRetina: true
      },

      snow: {
        fullScreen: { enable: false },
        background: { color: { value: 'transparent' } },
        fpsLimit: 60,
        particles: {
          number: { value: 150, density: { enable: true, width: 800, height: 800 } },
          color: { value: ['#ffffff', '#f0f8ff', '#f5f5f5'] },
          shape: { type: 'circle' },
          opacity: { value: { min: 0.4, max: 0.9 } },
          size: { value: { min: 1, max: 4 } },
          links: { enable: false },
          move: { enable: true, speed: 1, direction: 'bottom', random: true, outModes: { default: 'out' } }
        },
        interactivity: {
          detectsOn: 'canvas',
          events: {
            onHover: { enable: true, mode: 'bubble' },
            onClick: { enable: true, mode: 'push' }
          },
          modes: {
            bubble: { distance: 200, size: 6, duration: 2, opacity: 0.8 },
            push: { quantity: 4 }
          }
        },
        detectRetina: true
      },

      interactive: {
        fullScreen: { enable: false },
        background: { color: { value: 'transparent' } },
        fpsLimit: 60,
        particles: {
          number: { value: 80, density: { enable: true, width: 800, height: 800 } },
          color: { value: ['#ff0000', '#00ff00', '#0000ff', '#ffff00', '#ff00ff'] },
          shape: { type: 'circle' },
          opacity: { value: 1 },
          size: { value: { min: 4, max: 10 } },
          links: { enable: true, distance: 100, color: '#ffffff', opacity: 0.8, width: 2 },
          move: { enable: true, speed: 6, direction: 'none', random: true, outModes: { default: 'bounce' } }
        },
        interactivity: {
          detectsOn: 'canvas',
          events: {
            onHover: { enable: true, mode: 'repulse' },
            onClick: { enable: true, mode: 'bubble' }
          },
          modes: {
            repulse: { distance: 100, duration: 0.2 },
            bubble: { distance: 200, size: 20, duration: 1, opacity: 1 }
          }
        },
        detectRetina: true
      },

      demo: {
        fullScreen: { enable: false },
        background: { color: { value: 'transparent' } },
        fpsLimit: 60,
        particles: {
          number: { value: 50, density: { enable: true, width: 800, height: 800 } },
          color: { value: ['#ff0000', '#00ff00', '#0000ff', '#ffff00', '#ff00ff', '#00ffff'] },
          shape: { type: 'circle' },
          opacity: { value: 1 },
          size: { value: { min: 6, max: 14 } },
          links: { enable: true, distance: 80, color: '#ffffff', opacity: 1, width: 3 },
          move: { enable: true, speed: 8, direction: 'none', random: true, outModes: { default: 'bounce' } }
        },
        interactivity: {
          detectsOn: 'canvas',
          events: {
            onHover: { enable: true, mode: 'grab' },
            onClick: { enable: true, mode: 'bubble' }
          },
          modes: {
            grab: { distance: 150, links: { opacity: 1 } },
            bubble: { distance: 150, size: 30, duration: 0.5, opacity: 1 }
          }
        },
        detectRetina: true
      }
    };

    return themes[theme] || themes.default;
  }

  getAvailableThemes(): string[] {
    return ['default', 'cosmic', 'tech', 'nature', 'fire', 'snow', 'interactive', 'demo'];
  }

  getThemeDisplayName(theme: string): string {
    const names: Record<string, string> = {
      default: 'Default',
      cosmic: 'Cósmico',
      tech: 'Tecnología',
      nature: 'Naturaleza',
      fire: 'Fuego',
      snow: 'Nieve',
      interactive: 'Interactivo',
      demo: 'Demo'
    };
    return names[theme] || theme;
  }

  getThemeIcon(theme: string): string {
    const icons: Record<string, string> = {
      default: 'fas fa-circle',
      cosmic: 'fas fa-star',
      tech: 'fas fa-microchip',
      nature: 'fas fa-leaf',
      fire: 'fas fa-fire',
      snow: 'fas fa-snowflake',
      interactive: 'fas fa-mouse-pointer',
      demo: 'fas fa-play-circle'
    };
    return icons[theme] || 'fas fa-circle';
  }
}
